import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LaunchIcon from "@mui/icons-material/Launch";
import { useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import {
  UITableShell,
  UITableToolbar,
  UIStatusChip,
} from "../../../../components/ui";
import {
  convertMarketingProspect,
  getMarketingProspectConversionPreview,
  getMarketingProspects,
} from "../../../../services/crm";

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "lead", label: "Lead" },
  { value: "contact", label: "Contact" },
  { value: "organization", label: "Organisation" },
];

const PROSPECT_COLUMNS = [
  { id: "name", label: "Name", minWidth: 180 },
  { id: "type", label: "Type", minWidth: 120 },
  { id: "company", label: "Company", minWidth: 180 },
  { id: "title", label: "Title", minWidth: 160 },
  { id: "email", label: "Email", minWidth: 220 },
  { id: "phone", label: "Phone", minWidth: 160 },
  { id: "tags", label: "Tags", minWidth: 220 },
  { id: "leadStatus", label: "Lead Status", minWidth: 150 },
  { id: "rating", label: "Rating / Score", minWidth: 120 },
  { id: "converted", label: "Converted", minWidth: 110 },
  { id: "lastActivity", label: "Last Activity", minWidth: 170 },
  { id: "updated", label: "Updated", minWidth: 170 },
];

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || fallback;
}

function logApiError(context, error) {
  // Keep UI messages concise while preserving response details for debugging.
  // eslint-disable-next-line no-console
  console.error(context, {
    status: error?.response?.status,
    url: error?.config?.url,
    method: error?.config?.method,
    response: error?.response?.data,
    message: error?.message,
  });
}

function formatDateTime(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function toTitleCase(value) {
  const normalized = String(value || "")
    .trim()
    .replace(/[_-]+/g, " ");

  if (!normalized) return "-";

  return normalized.replace(/\b\w/g, (match) => match.toUpperCase());
}

function normalizeType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["organization", "organisation", "organizations", "organisations"].includes(normalized)) {
    return "organization";
  }
  return normalized;
}

function getRawValue(rawPayload = {}, keys = []) {
  for (const key of keys) {
    const value = rawPayload?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
}

function getProspectName(row) {
  if (row?.displayName) return row.displayName;

  const firstName = getRawValue(row?.rawPayload, ["FIRST_NAME"]);
  const lastName = getRawValue(row?.rawPayload, ["LAST_NAME"]);
  const orgName = getRawValue(row?.rawPayload, ["ORGANISATION_NAME", "ORGANIZATION_NAME"]);
  const joinedName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return joinedName || orgName || "-";
}

function getProspectCompany(row) {
  return (
    row?.companyName ||
    getRawValue(row?.rawPayload, ["ORGANISATION_NAME", "ORGANIZATION_NAME"]) ||
    "-"
  );
}

function getProspectTitle(row) {
  return getRawValue(row?.rawPayload, ["TITLE"]) || "-";
}

function getProspectEmail(row) {
  return row?.email || getRawValue(row?.rawPayload, ["EMAIL", "EMAIL_ADDRESS"]) || "-";
}

function getProspectPhone(row) {
  return row?.phone || getRawValue(row?.rawPayload, ["PHONE", "MOBILE"]) || "-";
}

function getProspectTags(row) {
  const tags = row?.rawPayload?.TAGS;
  if (Array.isArray(tags)) {
    return tags.filter(Boolean).map((tag) => String(tag).trim()).filter(Boolean);
  }

  if (typeof tags === "string" && tags.trim()) {
    return tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
}

function getLeadStatus(row) {
  return row?.stageKey || getRawValue(row?.rawPayload, ["LEAD_STATUS_ID"]) || "-";
}

function getProspectRating(row) {
  return getRawValue(row?.rawPayload, ["LEAD_RATING", "PROBABILITY"]) || "-";
}

function getConvertedValue(row) {
  const conversionStatus = String(row?.conversionStatus || "").toLowerCase();
  if (["converted", "linked"].includes(conversionStatus)) {
    return toTitleCase(conversionStatus);
  }

  const converted = row?.rawPayload?.CONVERTED;
  if (typeof converted === "boolean") {
    return converted ? "Yes" : "No";
  }
  return "-";
}

function getLastActivity(row) {
  return row?.rawPayload?.LAST_ACTIVITY_DATE_UTC || null;
}

function getStatusTone(value) {
  switch (String(value || "").toLowerCase()) {
    case "success":
    case "converted":
    case "linked":
    case "yes":
    case "qualified":
      return "success";
    case "contact":
    case "running":
      return "info";
    case "lead":
    case "open":
    case "new":
      return "warning";
    case "organization":
    case "organisation":
    case "contacted":
      return "accent";
    case "failed":
    case "error":
    case "archived":
    case "no":
      return "error";
    default:
      return "neutral";
  }
}

function getSummaryValue(response) {
  return Number(response?.data?.total) || 0;
}

function getAddressFields(rawPayload = {}) {
  const fields = [
    ["Street", getRawValue(rawPayload, ["ADDRESS_STREET"])],
    ["City", getRawValue(rawPayload, ["ADDRESS_CITY"])],
    ["State", getRawValue(rawPayload, ["ADDRESS_STATE"])],
    ["Postcode", getRawValue(rawPayload, ["ADDRESS_POSTCODE"])],
    ["Country", getRawValue(rawPayload, ["ADDRESS_COUNTRY"])],
    ["Mailing Street", getRawValue(rawPayload, ["ADDRESS_MAIL_STREET", "MAIL_STREET"])],
    ["Mailing City", getRawValue(rawPayload, ["ADDRESS_MAIL_CITY", "MAIL_CITY"])],
    ["Mailing State", getRawValue(rawPayload, ["ADDRESS_MAIL_STATE", "MAIL_STATE"])],
    ["Mailing Postcode", getRawValue(rawPayload, ["ADDRESS_MAIL_POSTCODE", "MAIL_POSTCODE"])],
    ["Mailing Country", getRawValue(rawPayload, ["ADDRESS_MAIL_COUNTRY", "MAIL_COUNTRY"])],
    ["Other Street", getRawValue(rawPayload, ["ADDRESS_OTHER_STREET", "OTHER_STREET"])],
    ["Other City", getRawValue(rawPayload, ["ADDRESS_OTHER_CITY", "OTHER_CITY"])],
    ["Other State", getRawValue(rawPayload, ["ADDRESS_OTHER_STATE", "OTHER_STATE"])],
    ["Other Postcode", getRawValue(rawPayload, ["ADDRESS_OTHER_POSTCODE", "OTHER_POSTCODE"])],
    ["Other Country", getRawValue(rawPayload, ["ADDRESS_OTHER_COUNTRY", "OTHER_COUNTRY"])],
  ];

  return fields.filter(([, value]) => value);
}

function getSourceIdentifiers(rawPayload = {}) {
  const entries = [
    ["Lead ID", getRawValue(rawPayload, ["LEAD_ID"])],
    ["Contact ID", getRawValue(rawPayload, ["CONTACT_ID"])],
    ["Organisation ID", getRawValue(rawPayload, ["ORGANISATION_ID", "ORGANIZATION_ID"])],
    ["Opportunity ID", getRawValue(rawPayload, ["OPPORTUNITY_ID"])],
    ["Owner User ID", getRawValue(rawPayload, ["OWNER_USER_ID"])],
    ["Responsible User ID", getRawValue(rawPayload, ["RESPONSIBLE_USER_ID"])],
    ["Record ID", getRawValue(rawPayload, ["ID"])],
  ];

  return entries.filter(([, value]) => value);
}

function getLinks(rawPayload = {}) {
  const links = rawPayload?.LINKS;
  if (Array.isArray(links)) {
    return links;
  }
  if (links && typeof links === "object") {
    return Object.entries(links).map(([key, value]) => ({ key, value }));
  }
  return [];
}

function getCustomFields(rawPayload = {}) {
  const fields = rawPayload?.CUSTOMFIELDS || rawPayload?.CUSTOM_FIELDS || [];
  return Array.isArray(fields) ? fields : [];
}

// function SummaryCard({ label, value, tone = "neutral" }) {
//   return (
//     <Card
//       variant="outlined"
//       sx={{
//         borderRadius: 2,
//         borderColor: "divider",
//         boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
//       }}
//     >
//       <CardContent sx={{ p: 2 }}>
//         <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
//           <Box>
//             <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.4 }}>
//               {label}
//             </Typography>
//             <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
//               {value}
//             </Typography>
//           </Box>
//           <UIStatusChip size="small" tone={tone} label={label} />
//         </Stack>
//       </CardContent>
//     </Card>
//   );
// }

function DetailRow({ label, value }) {
  if (!value || value === "-") return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "140px minmax(0, 1fr)",
        gap: 1.5,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.primary", wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
}

function SummaryList({ items = [] }) {
  const visible = items.filter((item) => item?.value !== undefined && item?.value !== null && String(item.value).trim() !== "");
  if (!visible.length) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        No additions available.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75}>
      {visible.map((item) => (
        <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
            {item.label}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.primary", textAlign: "right", wordBreak: "break-word" }}>
            {Array.isArray(item.value) ? item.value.join(", ") : String(item.value)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function getPreviewAdditionItems(preview) {
  const normalized = preview?.normalizedPayload || {};
  const proposed = preview?.proposedCustomerAdditions || {};
  const address = normalized.address || {};
  return [
    { label: "Business", value: proposed.businessName || normalized.companyName },
    { label: "Email", value: proposed.emailAddress || normalized.email },
    { label: "Phone", value: proposed.phoneNumber || normalized.phone },
    { label: "Website", value: normalized.website },
    { label: "Contact", value: [normalized.firstName, normalized.lastName].filter(Boolean).join(" ") },
    { label: "Industry", value: normalized.industry },
    { label: "Lead Source", value: normalized.leadSource || "insightly" },
    { label: "Address", value: [address.street, address.city, address.state, address.postal, address.country].filter(Boolean).join(", ") },
  ];
}

function isAlreadyLinkedPreview(preview) {
  const status = String(preview?.conversionStatus || "").toLowerCase();
  return Boolean(preview?.linkedCustomerId) || ["converted", "linked"].includes(status);
}

function getLinkedCustomerLabel(preview) {
  const linked = preview?.linkedCustomerSummary;
  if (linked?.businessName) return `${linked.businessName} #${linked.id}`;
  if (linked?.emailAddress) return `${linked.emailAddress} #${linked.id}`;
  if (preview?.linkedCustomerId) return `Customer #${preview.linkedCustomerId}`;
  return "";
}

export default function InstanceProspects() {
  const theme = useTheme();
  const { instanceUniqueId } = useParams();
  // const C = {
  //   paperBg: theme.palette.background.paper,
  //   subtleBg: theme.palette.action.hover,
  //   border: theme.palette.divider,
  //   textPri: theme.palette.text.primary,
  //   textSec: theme.palette.text.secondary,
  //   textMuted: theme.palette.text.disabled,
  //   white: theme.palette.common.white,
  //   headerBg: theme.eliteCore?.tokens?.color?.neutral?.[800] ?? "#1e293b",
  //   headerBgHover: theme.eliteCore?.tokens?.color?.neutral?.[700] ?? "#334155",
  // };

    const C = {
    paperBg: theme.palette.background.paper,
    subtleBg: theme.palette.action.hover,
    border: theme.palette.divider,
    textPri: theme.palette.text.primary,
    textSec: theme.palette.text.secondary,
    textMuted: theme.palette.text.disabled,
    white: theme.palette.common.white,
    primary: theme.palette.primary.main,
    primaryDark: theme.palette.primary.dark,
    primaryLight:
      theme.palette.mode === "dark"
        ? theme.palette.primary.dark
        : theme.palette.primary.light,
    primarySoft:
      theme.palette.mode === "dark"
        ? "rgba(239, 68, 68, 0.16)"
        : "rgba(239, 68, 68, 0.08)",
    primarySoftBorder:
      theme.palette.mode === "dark"
        ? "rgba(239, 68, 68, 0.28)"
        : "rgba(239, 68, 68, 0.18)",
    inputBg:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.04)"
        : "var(--ec-input-bg, #f8fafc)",
    headerBg: theme.eliteCore?.tokens?.color?.neutral?.[800] ?? "#1e293b",
    headerBgHover: theme.eliteCore?.tokens?.color?.neutral?.[700] ?? "#334155",
  };



   const compactFilterSx = {
    flex: 1,
    minWidth: 170,
    "& .MuiOutlinedInput-root": {
      height: 40,
      backgroundColor: C.inputBg,
      borderRadius: "10px",
      fontSize: "0.875rem",
      "& fieldset": {
        borderColor: C.border,
      },
      "&:hover fieldset": {
        borderColor: C.textMuted,
      },
      "&.Mui-focused": {
        backgroundColor: C.paperBg,
      },
      "&.Mui-focused fieldset": {
        borderColor: C.primary,
        borderWidth: "1px",
      },
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      minHeight: "40px",
    },
  };

  const summaryChipSx = {
    fontWeight: 700,
    fontSize: "0.72rem",
    height: 24,
    cursor: "pointer",
    borderRadius: "999px",
    transition: "all 0.15s ease",
  };

  const activeSummaryChipSx = {
    backgroundColor: C.primarySoft,
    color: C.primary,
    border: `1px solid ${C.primarySoftBorder}`,
    "&:hover": {
      backgroundColor: C.primarySoft,
    },
  };

  const neutralSummaryChipSx = {
    backgroundColor: "var(--ec-status-neutral-bg)",
    color: "var(--ec-status-neutral-text)",
    border: "1px solid var(--ec-status-neutral-border)",
    "&:hover": {
      backgroundColor: C.subtleBg,
    },
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [providerFilter, setProviderFilter] = useState("insightly");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryCounts, setSummaryCounts] = useState({
    total: 0,
    leads: 0,
    contacts: 0,
    organisations: 0,
  });
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [conversionPreview, setConversionPreview] = useState(null);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState("");
  const [conversionSuccess, setConversionSuccess] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [conversionSubmitting, setConversionSubmitting] = useState(false);

  const statusOptions = useMemo(() => {
    const values = Array.from(
      new Set(rows.map((row) => String(row?.status || "").trim()).filter(Boolean))
    );
    return values.sort((left, right) => left.localeCompare(right));
  }, [rows]);

  const loadProspects = async () => {
    if (!instanceUniqueId) return;

    setLoading(true);
    setError("");

    try {
      const response = await getMarketingProspects({
        instanceUniqueId,
        provider: providerFilter || undefined,
        q: searchQuery || undefined,
        status: statusFilter || undefined,
        externalType: typeFilter || undefined,
        page: page + 1,
        rpp: rowsPerPage,
      });

      setRows(Array.isArray(response.data?.results) ? response.data.results : []);
      setTotal(Number(response.data?.total) || 0);
    } catch (loadError) {
      setRows([]);
      setTotal(0);
      setError(getErrorMessage(loadError, "Failed to load synced prospects."));
    } finally {
      setLoading(false);
    }
  };

  const loadSummaryCounts = async () => {
    if (!instanceUniqueId) return;

    setSummaryLoading(true);
    try {
      const [totalRes, leadsRes, contactsRes, orgsRes] = await Promise.all([
        getMarketingProspects({
          instanceUniqueId,
          provider: providerFilter || undefined,
          page: 1,
          rpp: 1,
        }),
        getMarketingProspects({
          instanceUniqueId,
          provider: providerFilter || undefined,
          externalType: "lead",
          page: 1,
          rpp: 1,
        }),
        getMarketingProspects({
          instanceUniqueId,
          provider: providerFilter || undefined,
          externalType: "contact",
          page: 1,
          rpp: 1,
        }),
        getMarketingProspects({
          instanceUniqueId,
          provider: providerFilter || undefined,
          externalType: "organization",
          page: 1,
          rpp: 1,
        }),
      ]);

      setSummaryCounts({
        total: getSummaryValue(totalRes),
        leads: getSummaryValue(leadsRes),
        contacts: getSummaryValue(contactsRes),
        organisations: getSummaryValue(orgsRes),
      });
    } catch (_summaryError) {
      setSummaryCounts({
        total: 0,
        leads: 0,
        contacts: 0,
        organisations: 0,
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(0);
      setSearchQuery(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadProspects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId, providerFilter, typeFilter, statusFilter, searchQuery, page, rowsPerPage]);

  useEffect(() => {
    loadSummaryCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId, providerFilter]);

  useEffect(() => {
    if (!selectedProspect?.id || !instanceUniqueId) {
      setConversionPreview(null);
      setConversionError("");
      setConversionSuccess("");
      setSelectedCustomerId("");
      return;
    }

    const loadConversionPreview = async () => {
      setConversionLoading(true);
      setConversionError("");
      setConversionSuccess("");
      setSelectedCustomerId("");
      try {
        const response = await getMarketingProspectConversionPreview(instanceUniqueId, selectedProspect.id);
        const result = response.data?.result || null;
        setConversionPreview(result);
        const firstStrongCandidate = (result?.duplicateCandidates || []).find((candidate) => candidate.score >= 90);
        if (firstStrongCandidate?.id) {
          setSelectedCustomerId(String(firstStrongCandidate.id));
        }
      } catch (previewError) {
        logApiError("Failed to build prospect conversion preview", previewError);
        setConversionPreview(null);
        setConversionError(getErrorMessage(previewError, "Failed to build conversion preview."));
      } finally {
        setConversionLoading(false);
      }
    };

    loadConversionPreview();
  }, [instanceUniqueId, selectedProspect?.id]);

  const clearFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setTypeFilter("");
    setStatusFilter("");
    setPage(0);
  };

  const refreshSelectedProspectConversion = (result) => {
    if (!selectedProspect) return;
    const nextProspect = {
      ...selectedProspect,
      conversionStatus: result?.conversionStatus || selectedProspect.conversionStatus,
      linkedCustomerId: result?.customerId || selectedProspect.linkedCustomerId,
      convertedAt: new Date().toISOString(),
    };
    setSelectedProspect(nextProspect);
    setRows((currentRows) => currentRows.map((row) => (row.id === nextProspect.id ? { ...row, ...nextProspect } : row)));
  };

  const handleConvertProspect = async (action) => {
    if (!selectedProspect?.id || !instanceUniqueId) return;
    const needsCustomer = action === "link" || action === "update";
    if (needsCustomer && !selectedCustomerId) {
      setConversionError("Select a duplicate candidate before linking or updating.");
      return;
    }

    setConversionSubmitting(true);
    setConversionError("");
    setConversionSuccess("");
    try {
      const response = await convertMarketingProspect(instanceUniqueId, selectedProspect.id, {
        action,
        customerId: needsCustomer ? Number(selectedCustomerId) : undefined,
        confirmDuplicateOverride: false,
      });
      const result = response.data?.result || {};
      refreshSelectedProspectConversion(result);
      setConversionSuccess(
        result.noMaterialChanges
          ? "Prospect is already linked with no material changes."
          : "Prospect conversion details were applied."
      );
      const previewResponse = await getMarketingProspectConversionPreview(instanceUniqueId, selectedProspect.id);
      setConversionPreview(previewResponse.data?.result || null);
      loadProspects();
      loadSummaryCounts();
    } catch (convertError) {
      logApiError("Failed to convert prospect", convertError);
      setConversionError(getErrorMessage(convertError, "Failed to convert prospect."));
    } finally {
      setConversionSubmitting(false);
    }
  };

  const openLinkedCustomer = (customerId) => {
    if (!customerId || !instanceUniqueId) return;
    window.open(
      `/instance/${instanceUniqueId}/sales/customers?tab=all&openCustomer=${customerId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const summaryLabel = total
    ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, total)} of ${total}`
    : "No results";

  return (
    <div className="instance-dashboard instance-orders-page">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box
          sx={{
            p: { xs: 1, md: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            height: "calc(100vh - 60px)",
            overflow: "hidden",
          }}
        >
          {/* <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            {summaryLoading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <Card key={`summary-skeleton-${index}`} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Skeleton variant="text" width="45%" />
                    <Skeleton variant="text" width="35%" height={36} />
                  </CardContent>
                </Card>
              ))
            ) : (
              <>
                <SummaryCard label="Total" value={summaryCounts.total} tone="neutral" />
                <SummaryCard label="Leads" value={summaryCounts.leads} tone="warning" />
                <SummaryCard label="Contacts" value={summaryCounts.contacts} tone="info" />
                <SummaryCard label="Organisations" value={summaryCounts.organisations} tone="accent" />
              </>
            )}
          </Box> */}

          {error ? (
            <Alert
              severity="error"
              sx={{ flexShrink: 0 }}
              action={
                <Button color="inherit" size="small" onClick={loadProspects}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          ) : null}

          <UITableShell
            customLayout
            paperSx={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              borderTop: "3px solid",
              borderTopColor: "primary.main",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
            }}
          >
                      <UITableToolbar sx={{ backgroundColor: C.paperBg, flexShrink: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: C.textPri, lineHeight: 1, mr: 0.5 }}
                  >
                    Prospects
                  </Typography>

                  {summaryLoading ? (
                    <>
                      <Skeleton variant="rounded" width={64} height={24} sx={{ borderRadius: "999px" }} />
                      <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: "999px" }} />
                      <Skeleton variant="rounded" width={88} height={24} sx={{ borderRadius: "999px" }} />
                      <Skeleton variant="rounded" width={78} height={24} sx={{ borderRadius: "999px" }} />
                    </>
                  ) : (
                    <>
                      <Chip
                        label={`${summaryCounts.total} total`}
                        size="small"
                        onClick={() => {
                          setTypeFilter("");
                          setPage(0);
                        }}
                        sx={{
                          ...summaryChipSx,
                          ...(typeFilter ? neutralSummaryChipSx : activeSummaryChipSx),
                        }}
                      />

                      <Chip
                        label={`${summaryCounts.leads} Leads`}
                        size="small"
                        onClick={() => {
                          setTypeFilter(typeFilter === "lead" ? "" : "lead");
                          setPage(0);
                        }}
                        sx={{
                          ...summaryChipSx,
                          ...(typeFilter === "lead" ? activeSummaryChipSx : neutralSummaryChipSx),
                        }}
                      />

                      <Chip
                        label={`${summaryCounts.contacts} Contacts`}
                        size="small"
                        onClick={() => {
                          setTypeFilter(typeFilter === "contact" ? "" : "contact");
                          setPage(0);
                        }}
                        sx={{
                          ...summaryChipSx,
                          ...(typeFilter === "contact" ? activeSummaryChipSx : neutralSummaryChipSx),
                        }}
                      />

                      <Chip
                        label={`${summaryCounts.organisations} Orgs`}
                        size="small"
                        onClick={() => {
                          setTypeFilter(typeFilter === "organization" ? "" : "organization");
                          setPage(0);
                        }}
                        sx={{
                          ...summaryChipSx,
                          ...(typeFilter === "organization" ? activeSummaryChipSx : neutralSummaryChipSx),
                        }}
                      />

                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ height: 20, alignSelf: "center", mx: 0.25 }}
                      />

                      <Chip
                        size="small"
                        label={
                          providerFilter
                            ? `Provider: ${toTitleCase(providerFilter)}`
                            : "All Providers"
                        }
                        sx={{
                          ...summaryChipSx,
                          backgroundColor: C.inputBg,
                          color: C.textSec,
                          border: `1px solid ${C.border}`,
                          cursor: "default",
                        }}
                      />
                    </>
                  )}
                </Stack>

                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    loadProspects();
                    loadSummaryCounts();
                  }}
                  disabled={loading}
                  sx={{
                    fontWeight: 700,
                    textTransform: "none",
                    height: 34,
                    px: 2,
                    whiteSpace: "nowrap",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                >
                  Refresh
                </Button>
              </Stack>

              <Divider sx={{ mt: 1.75, mb: 0 }} />

              <Stack
                direction={{ xs: "column", xl: "row" }}
                spacing={1.25}
                mt={1.75}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
              >
                <TextField
                  size="small"
                  placeholder="Search by name, company, email, or phone"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: C.textMuted }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => {
                            setSearchInput("");
                            setSearchQuery("");
                            setPage(0);
                          }}
                        >
                          <ClearIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    flex: 2,
                    minWidth: 280,
                    maxWidth: 420,
                    "& .MuiOutlinedInput-root": {
                      height: 40,
                      backgroundColor: C.inputBg,
                      borderRadius: "10px",
                      "& fieldset": {
                        borderColor: C.border,
                      },
                      "&:hover fieldset": {
                        borderColor: C.textMuted,
                      },
                      "&.Mui-focused": {
                        backgroundColor: C.paperBg,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: C.primary,
                        borderWidth: "1px",
                      },
                    },
                  }}
                />

                <FormControl size="small" sx={compactFilterSx}>
                  <Select
                    displayEmpty
                    value={providerFilter}
                    onChange={(event) => {
                      setProviderFilter(event.target.value);
                      setPage(0);
                    }}
                    renderValue={(selected) =>
                      selected ? `Provider: ${toTitleCase(selected)}` : "All Providers"
                    }
                  >
                    <MenuItem value="insightly">Insightly</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={compactFilterSx}>
                  <Select
                    displayEmpty
                    value={typeFilter}
                    onChange={(event) => {
                      setTypeFilter(event.target.value);
                      setPage(0);
                    }}
                    renderValue={(selected) =>
                      selected
                        ? `Type: ${toTitleCase(
                            selected === "organization" ? "organisation" : selected
                          )}`
                        : "All Types"
                    }
                  >
                    {TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value || "all"} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={compactFilterSx}>
                  <Select
                    displayEmpty
                    value={statusFilter}
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setPage(0);
                    }}
                    renderValue={(selected) =>
                      selected ? `Status: ${toTitleCase(selected)}` : "All Statuses"
                    }
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {statusOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {toTitleCase(option)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(searchQuery || statusFilter || typeFilter) ? (
                  <Tooltip title="Clear all filters">
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                      onClick={clearFilters}
                      sx={{
                        height: 40,
                        textTransform: "none",
                        fontWeight: 600,
                        color: "text.secondary",
                        px: 1.5,
                        "&:hover": {
                          color: "error.main",
                          backgroundColor: "var(--ec-status-error-bg)",
                        },
                      }}
                    >
                      Clear
                    </Button>
                  </Tooltip>
                ) : null}
              </Stack>

              <Typography
                variant="body2"
                sx={{ mt: 1.25, color: C.textMuted, fontSize: "0.8125rem" }}
              >
                <strong style={{ color: C.textSec }}>Prospects:</strong> {total} row
                {total !== 1 ? "s" : ""} - Click a row to inspect source details
              </Typography>

              {(searchQuery || statusFilter || typeFilter) ? (
                <Stack
                  direction="row"
                  spacing={0.75}
                  mt={1.25}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="center"
                >
                  <Typography
                    variant="caption"
                    sx={{ color: C.textMuted, fontWeight: 600, mr: 0.25 }}
                  >
                    Active filters:
                  </Typography>

                  {searchQuery ? (
                    <Chip
                      label={`Search: "${searchQuery}"`}
                      size="small"
                      onDelete={() => {
                        setSearchInput("");
                        setSearchQuery("");
                        setPage(0);
                      }}
                      sx={{
                        backgroundColor: C.primarySoft,
                        color: C.primary,
                        border: `1px solid ${C.primarySoftBorder}`,
                        fontWeight: 600,
                        fontSize: "0.72rem",
                      }}
                    />
                  ) : null}

                  {typeFilter ? (
                    <Chip
                      size="small"
                      label={toTitleCase(
                        typeFilter === "organization" ? "organisation" : typeFilter
                      )}
                      onDelete={() => {
                        setTypeFilter("");
                        setPage(0);
                      }}
                      sx={{
                        backgroundColor: C.primarySoft,
                        color: C.primary,
                        border: `1px solid ${C.primarySoftBorder}`,
                        fontWeight: 600,
                        fontSize: "0.72rem",
                      }}
                    />
                  ) : null}

                  {statusFilter ? (
                    <Chip
                      size="small"
                      label={toTitleCase(statusFilter)}
                      onDelete={() => {
                        setStatusFilter("");
                        setPage(0);
                      }}
                      sx={{
                        backgroundColor: "var(--ec-status-neutral-bg)",
                        color: "var(--ec-status-neutral-text)",
                        border: "1px solid var(--ec-status-neutral-border)",
                        fontWeight: 600,
                        fontSize: "0.72rem",
                      }}
                    />
                  ) : null}
                </Stack>
              ) : null}
            </UITableToolbar>

            <TableContainer
              sx={{
                flex: 1,
                overflow: "auto",
                minHeight: 0,
                //  minHeight: "500px",
                    //  minHeight: "1000px",
                // height: "100vh",
                // overflow: "hidden",
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--ec-color-border-strong, #cbd5e1)",
                  borderRadius: 99,
                },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 1720, width: "100%" }}>
                <TableHead>
                  <TableRow>
                    {PROSPECT_COLUMNS.map((column) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          minWidth: column.minWidth,
                          px: 2,
                          py: 1.25,
                          backgroundColor: `${C.headerBg} !important`,
                          color: "rgba(255,255,255,0.65)",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          borderBottom: "none",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          transition: "all 0.15s",
                          "&:hover": {
                            backgroundColor: `${C.headerBgHover} !important`,
                            color: "#fff",
                          },
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: `1px solid ${C.border}`,
                    },
                    "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                      borderBottom: "none",
                    },
                  }}
                >
                  {loading
                    ? Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={`prospect-skeleton-${index}`}>
                          {PROSPECT_COLUMNS.map((column) => (
                            <TableCell key={column.id} sx={{ px: 2, py: 1.5 }}>
                              <Skeleton
                                variant="rectangular"
                                height={16}
                                sx={{ borderRadius: 1, opacity: Math.max(0.15, 1 - index * 0.08) }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : null}

                  {!loading && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={PROSPECT_COLUMNS.length} align="center" sx={{ py: 10, borderBottom: "none" }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              backgroundColor: "var(--ec-color-surface-muted)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FolderOffIcon sx={{ fontSize: 32, color: "var(--ec-color-text-muted)" }} />
                          </Box>

                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                              No prospects found
                            </Typography>
                            <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 360 }}>
                              {searchQuery || statusFilter || typeFilter
                                ? "No synced prospects match the current filters. Try adjusting the search or filters."
                                : "No synced prospects are available yet. Run a sync from Marketing Connections to populate this page."}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={1.5} mt={0.5}>
                            {(searchQuery || statusFilter || typeFilter) ? (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                                onClick={clearFilters}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                              >
                                Clear Filters
                              </Button>
                            ) : null}
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                              onClick={() => {
                                loadProspects();
                                loadSummaryCounts();
                              }}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              Refresh
                            </Button>
                          </Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!loading &&
                    rows.map((row) => {
                      const tags = getProspectTags(row);
                      const visibleTags = tags.slice(0, 2);
                      const hiddenCount = Math.max(tags.length - visibleTags.length, 0);

                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => setSelectedProspect(row)}
                          sx={{
                            cursor: "pointer",
                            backgroundColor: C.paperBg,
                            transition: "background-color 0.1s",
                            "&:hover": { backgroundColor: C.subtleBg },
                          }}
                        >
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ minWidth: 0 }}>
                                {/* <Typography variant="body2" sx={{ color: C.textPri, fontWeight: 600 }}>
                                  {getProspectName(row)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: C.textMuted }}>
                                  {row.externalId || "-"}
                                </Typography> */}

                                <Typography
  variant="body2"
  sx={{
    color: C.textPri,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }}
>
  {getProspectName(row)}
</Typography>
<Typography
  variant="caption"
  sx={{
    color: C.primary,
    fontWeight: 700,
    fontSize: "0.68rem",
    display: "block",
    lineHeight: 1.3,
  }}
>
  {row.externalId || "-"}
</Typography>
                              </Box>
                              <Tooltip title="View details">
                                <VisibilityIcon sx={{ fontSize: 16, color: C.textMuted }} />
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <UIStatusChip
                              size="small"
                              tone={getStatusTone(normalizeType(row.externalType))}
                              label={toTitleCase(
                                normalizeType(row.externalType) === "organization"
                                  ? "organisation"
                                  : row.externalType
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            {/* <Typography variant="body2" sx={{ color: C.textPri }}>
                              {getProspectCompany(row)}
                            </Typography> */}
                            <Typography
  variant="body2"
  sx={{
    color: C.textPri,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }}
>
  {getProspectCompany(row)}
</Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="body2" sx={{ color: C.textSec }}>
                              {getProspectTitle(row)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="body2" sx={{ color: C.textSec }}>
                              {getProspectEmail(row)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="body2" sx={{ color: C.textSec }}>
                              {getProspectPhone(row)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            {tags.length ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {visibleTags.map((tag) => (
                                  <Chip
                                    key={`${row.id}-${tag}`}
                                    size="small"
                                    label={tag}
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      backgroundColor: "var(--ec-status-neutral-bg)",
                                      color: "var(--ec-status-neutral-text)",
                                      border: "1px solid var(--ec-status-neutral-border)",
                                    }}
                                  />
                                ))}
                                {hiddenCount > 0 ? (
                                  // <Chip
                                  //   size="small"
                                  //   label={`+${hiddenCount}`}
                                  //   sx={{
                                  //     height: 22,
                                  //     fontSize: "0.7rem",
                                  //     backgroundColor: "var(--ec-status-info-bg)",
                                  //     color: "var(--ec-status-info-text)",
                                  //     border: "1px solid var(--ec-status-info-border)",
                                  //   }}
                                  // />
                                  <Chip
  size="small"
  label={`+${hiddenCount}`}
  sx={{
    height: 22,
    fontSize: "0.7rem",
    backgroundColor: C.primarySoft,
    color: C.primary,
    border: `1px solid ${C.primarySoftBorder}`,
  }}
/>
                                ) : null}
                              </Stack>
                            ) : (
                              <Typography variant="body2" sx={{ color: C.textMuted }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <UIStatusChip
                              size="small"
                              tone={getStatusTone(getLeadStatus(row))}
                              label={toTitleCase(getLeadStatus(row))}
                            />
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            {/* <Typography variant="body2" sx={{ color: C.textPri, fontWeight: 600 }}>
                              {getProspectRating(row)}
                            </Typography> */}
                            <Typography
  variant="body2"
  sx={{
    color: getProspectRating(row) !== "-" ? C.primary : C.textMuted,
    fontWeight: 700,
  }}
>
  {getProspectRating(row)}
</Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            {getConvertedValue(row) !== "-" ? (
                              <UIStatusChip
                                size="small"
                                tone={getConvertedValue(row) === "Yes" ? "success" : "neutral"}
                                label={getConvertedValue(row)}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: C.textMuted }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="body2" sx={{ color: C.textSec }}>
                              {formatDateTime(getLastActivity(row))}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="body2" sx={{ color: C.textSec }}>
                              {formatDateTime(row.sourceUpdatedUtc || row.updatedAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                px: 2,
                backgroundColor: C.paperBg,
                borderTop: `1px solid ${C.border}`,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 52,
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: C.textMuted, fontSize: "0.8125rem", pl: 1 }}
              >
                {summaryLabel}
              </Typography>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  border: 0,
                  "& .MuiTablePagination-toolbar": { minHeight: 52, px: 0 },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "0.8125rem",
                    color: `${C.textMuted} !important`,
                    mb: 0,
                  },
                }}
              />
            </Box>
          </UITableShell>
        </Box>
      </div>

      <Drawer
        anchor="right"
        open={Boolean(selectedProspect)}
        onClose={() => setSelectedProspect(null)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 480, md: 560 },
            p: 0,
          },
        }}
      >
        {selectedProspect ? (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {getProspectName(selectedProspect)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    {toTitleCase(
                      normalizeType(selectedProspect.externalType) === "organization"
                        ? "organisation"
                        : selectedProspect.externalType
                    )}{" "}
                    - {getProspectCompany(selectedProspect)}
                  </Typography>
                </Box>
                <Button color="inherit" onClick={() => setSelectedProspect(null)}>
                  Close
                </Button>
              </Stack>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", p: 2.5 }}>
              <Stack spacing={2}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Basic Details
                    </Typography>
                    <DetailRow label="Name" value={getProspectName(selectedProspect)} />
                    <DetailRow
                      label="Type"
                      value={toTitleCase(
                        normalizeType(selectedProspect.externalType) === "organization"
                          ? "organisation"
                          : selectedProspect.externalType
                      )}
                    />
                    <DetailRow label="Company" value={getProspectCompany(selectedProspect)} />
                    <DetailRow label="Title" value={getProspectTitle(selectedProspect)} />
                    <DetailRow label="Email" value={getProspectEmail(selectedProspect)} />
                    <DetailRow label="Phone" value={getProspectPhone(selectedProspect)} />
                    <DetailRow label="Lead Status" value={toTitleCase(getLeadStatus(selectedProspect))} />
                    <DetailRow label="Rating / Score" value={getProspectRating(selectedProspect)} />
                    <DetailRow label="Converted" value={getConvertedValue(selectedProspect)} />
                    <DetailRow label="Last Activity" value={formatDateTime(getLastActivity(selectedProspect))} />
                    <DetailRow
                      label="Updated"
                      value={formatDateTime(selectedProspect.sourceUpdatedUtc || selectedProspect.updatedAt)}
                    />
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} mb={1.5}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Customer Conversion
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Preview and operator-controlled Customer linking
                        </Typography>
                      </Box>
                      {conversionPreview?.linkedCustomerId ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openLinkedCustomer(conversionPreview.linkedCustomerId)}
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          Open Customer
                        </Button>
                      ) : null}
                    </Stack>

                    {conversionLoading ? (
                      <Stack spacing={1}>
                        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" height={96} sx={{ borderRadius: 1 }} />
                      </Stack>
                    ) : null}

                    {!conversionLoading && conversionError ? (
                      <Alert severity="error" sx={{ mb: 1.5 }}>
                        {conversionError}
                      </Alert>
                    ) : null}

                    {!conversionLoading && conversionSuccess ? (
                      <Alert severity="success" sx={{ mb: 1.5 }}>
                        {conversionSuccess}
                      </Alert>
                    ) : null}

                    {!conversionLoading && conversionPreview ? (
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <UIStatusChip
                            size="small"
                            tone={conversionPreview.conversionSupported ? "success" : "info"}
                            label={conversionPreview.conversionSupported ? "Conversion Supported" : "Unsupported Type"}
                          />
                          <UIStatusChip
                            size="small"
                            tone={getStatusTone(conversionPreview.conversionStatus)}
                            label={toTitleCase(conversionPreview.conversionStatus || "not_converted")}
                          />
                          {conversionPreview.noMaterialChanges ? (
                            <UIStatusChip size="small" tone="info" label="No Material Changes" />
                          ) : null}
                          {conversionPreview.rawPayloadAvailable ? (
                            <UIStatusChip size="small" tone="neutral" label="Raw Payload Available" />
                          ) : null}
                        </Stack>

                        {!conversionPreview.conversionSupported ? (
                          <Alert severity="info">
                            {conversionPreview.unsupportedReason ||
                              "Customer conversion is currently enabled for Insightly Prospects/Leads. This record can be reviewed here, but conversion is not enabled for this type in Phase 1."}
                          </Alert>
                        ) : null}

                        {conversionPreview.conversionSupported && conversionPreview.noMaterialChanges ? (
                          <Alert severity="info">
                            Prospect is already linked with no material changes.
                          </Alert>
                        ) : null}

                        {conversionPreview.linkedCustomerId ? (
                          <Box sx={{ p: 1.25, borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block" }}>
                              Linked Customer
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 700 }}>
                              {getLinkedCustomerLabel(conversionPreview)}
                            </Typography>
                          </Box>
                        ) : null}

                        <Box sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "var(--ec-color-surface-muted)" }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 1 }}>
                            Proposed additions
                          </Typography>
                          <SummaryList items={getPreviewAdditionItems(conversionPreview)} />
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 1 }}>
                            Duplicate candidates
                          </Typography>
                          {conversionPreview.duplicateCandidates?.length ? (
                            <Stack spacing={1}>
                              {conversionPreview.duplicateCandidates.slice(0, 5).map((candidate) => {
                                const selected = String(candidate.id) === String(selectedCustomerId);
                                return (
                                  <Box
                                    key={candidate.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedCustomerId(String(candidate.id))}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        setSelectedCustomerId(String(candidate.id));
                                      }
                                    }}
                                    sx={{
                                      p: 1.25,
                                      borderRadius: 1.5,
                                      border: "1px solid",
                                      borderColor: selected ? "primary.main" : "divider",
                                      backgroundColor: selected ? "var(--ec-status-info-bg)" : "background.paper",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                          {candidate.businessName || candidate.emailAddress || `Customer #${candidate.id}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                          #{candidate.id} - {candidate.emailAddress || "No email"} - {candidate.phoneNumber || "No phone"}
                                        </Typography>
                                      </Box>
                                      <UIStatusChip
                                        size="small"
                                        tone={candidate.score >= 90 ? "success" : candidate.score >= 70 ? "warning" : "neutral"}
                                        label={`${candidate.score}`}
                                      />
                                    </Stack>
                                    {candidate.reasons?.length ? (
                                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
                                        {candidate.reasons.join(" ")}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                );
                              })}
                            </Stack>
                          ) : (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              No duplicate candidates found.
                            </Typography>
                          )}
                        </Box>

                        {conversionPreview.conflicts?.length ? (
                          <Alert severity="warning">
                            {conversionPreview.conflicts.map((conflict) => (
                              <Typography key={conflict.field} variant="body2">
                                {conflict.field}: existing "{String(conflict.currentValue)}", incoming "{String(conflict.incomingValue)}"
                              </Typography>
                            ))}
                          </Alert>
                        ) : null}

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={
                              conversionSubmitting ||
                              !conversionPreview.conversionSupported ||
                              isAlreadyLinkedPreview(conversionPreview) ||
                              conversionPreview.noMaterialChanges ||
                              conversionPreview.duplicateCandidates?.some((candidate) => candidate.score >= 90)
                            }
                            onClick={() => handleConvertProspect("create")}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Create New Customer
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={
                              conversionSubmitting ||
                              !conversionPreview.conversionSupported ||
                              isAlreadyLinkedPreview(conversionPreview) ||
                              conversionPreview.noMaterialChanges ||
                              !selectedCustomerId
                            }
                            onClick={() => handleConvertProspect("link")}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Link Selected Customer
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={
                              conversionSubmitting ||
                              !conversionPreview.conversionSupported ||
                              conversionPreview.noMaterialChanges ||
                              !selectedCustomerId
                            }
                            onClick={() => handleConvertProspect("update")}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Update Selected Customer
                          </Button>
                        </Stack>
                      </Stack>
                    ) : null}
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Address
                    </Typography>
                    {getAddressFields(selectedProspect.rawPayload).length ? (
                      getAddressFields(selectedProspect.rawPayload).map(([label, value]) => (
                        <DetailRow key={label} label={label} value={value} />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        No address details available.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Source IDs and Ownership
                    </Typography>
                    {getSourceIdentifiers(selectedProspect.rawPayload).length ? (
                      getSourceIdentifiers(selectedProspect.rawPayload).map(([label, value]) => (
                        <DetailRow key={label} label={label} value={value} />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        No source identifiers available.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Links
                    </Typography>
                    {getLinks(selectedProspect.rawPayload).length ? (
                      <Stack spacing={1}>
                        {getLinks(selectedProspect.rawPayload).map((linkItem, index) => {
                          const label =
                            linkItem?.LINK_OBJECT_NAME ||
                            linkItem?.OBJECT_NAME ||
                            linkItem?.key ||
                            `Link ${index + 1}`;
                          const value =
                            linkItem?.LINK_OBJECT_ID ||
                            linkItem?.OBJECT_ID ||
                            linkItem?.value ||
                            linkItem;
                          const maybeUrl = typeof value === "string" && /^https?:\/\//i.test(value);

                          return (
                            <Box
                              key={`${label}-${index}`}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                p: 1.25,
                                borderRadius: 1.5,
                                backgroundColor: "var(--ec-color-surface-muted)",
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {toTitleCase(label)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary", wordBreak: "break-all" }}>
                                  {String(value)}
                                </Typography>
                              </Box>
                              {maybeUrl ? (
                                <Tooltip title="Open link">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <LaunchIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        No linked opportunity or project records available.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Custom Fields
                    </Typography>
                    {getCustomFields(selectedProspect.rawPayload).length ? (
                      getCustomFields(selectedProspect.rawPayload).map((field, index) => (
                        <DetailRow
                          key={`${field?.FIELD_NAME || field?.CUSTOM_FIELD_ID || index}`}
                          label={field?.FIELD_NAME || field?.CUSTOM_FIELD_ID || `Custom ${index + 1}`}
                          value={field?.FIELD_VALUE || field?.VALUE || "-"}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        No custom fields available.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px !important", overflow: "hidden" }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Raw Payload JSON
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: "#0f172a",
                        color: "#e2e8f0",
                        fontSize: "0.78rem",
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {JSON.stringify(selectedProspect.rawPayload || {}, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Box>
          </Box>
        ) : null}
      </Drawer>
    </div>
  );
}
